import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cityCoordinates } from '../../../utils/locationData';

const LocationContext = createContext(null);

const useGeolocationState = () => {
    const [coordinates, setCoordinates] = useState(() => {
        try {
            return JSON.parse(sessionStorage.getItem('userCoordinates')) || null;
        } catch {
            return null;
        }
    });
    const [city, setCity] = useState(() => {
        const saved = localStorage.getItem('userSelectedCity');
        return saved || null;
    });
    const [locationSource, setLocationSource] = useState(() => {
       
        const saved = localStorage.getItem('locationSource');
        return saved || 'detected'; // default to detected for auto-detected on load
    });
    const [address, setAddress] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasLocationPermission, setHasLocationPermission] = useState(false);
    const [locationPermissionState, setLocationPermissionState] = useState('checking');
    const [selectedCity, setSelectedCity] = useState(() => {
    const saved = localStorage.getItem('userSelectedCity');
    return saved || null;
});
    const [showPermissionPrompt, setShowPermissionPrompt] = useState(() => {
        // Only show prompt if user has never interacted with location feature
        const hasPermissionBeenAsked = localStorage.getItem('locationPermissionAsked');
        const savedCity = localStorage.getItem('userSelectedCity');
        return !hasPermissionBeenAsked && !savedCity;
    });

    // Detect location using geolocation API
    const detectLocation = useCallback(() => {
        setLoading(true);
        setError(null);
        setShowPermissionPrompt(false);
        localStorage.setItem('locationPermissionAsked', 'true');

        navigator.geolocation.getCurrentPosition((pos) => {
            const { latitude, longitude } = pos.coords;
            const nextCoordinates = { latitude, longitude };
            setCoordinates(nextCoordinates);
            setHasLocationPermission(true);
            setLocationPermissionState('granted');
            sessionStorage.setItem('userCoordinates', JSON.stringify(nextCoordinates));
            

            const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`;

            fetch(url)
                .then((res) => res.json())
                .then((data) => {
                    setAddress(data.address);
                    

                    // Get city from response
                    const detectedCity = data.address?.city || 
                                        data.address?.town || 
                                        data.address?.village || 
                                        data.address?.state_district ||
                                        data.address?.county;

                    if (detectedCity) {
                        setSelectedCity(detectedCity);
                        setCity(detectedCity);
                        setLocationSource('detected');
                        localStorage.setItem('userSelectedCity', detectedCity);
                        localStorage.setItem('locationSource', 'detected');
                        window.dispatchEvent(new Event('locationChanged')); 
                    }
                    setLoading(false);
                })
                .catch((err) => {
                    console.error("Fetch error:", err);
                    setError("Failed to fetch location");
                    setLoading(false);
                });
        }, (err) => {
            console.error("Geolocation error:", err);
            setError("Location access denied");
            setHasLocationPermission(false);
            setLocationPermissionState(err.code === 1 ? 'denied' : 'prompt');
            setCoordinates(null);
            sessionStorage.removeItem('userCoordinates');
            setLoading(false);
        });
    }, []);

    // Saved coordinates are only usable while browser permission is still granted.
    useEffect(() => {
        if (!navigator.permissions?.query) {
            setHasLocationPermission(Boolean(coordinates));
            setLocationPermissionState(coordinates ? 'granted' : 'prompt');
            return undefined;
        }

        let permissionStatus;
        const syncPermission = (status) => {
            const granted = status.state === 'granted';
            setHasLocationPermission(granted);
            setLocationPermissionState(status.state);
            if (!granted) {
                setCoordinates(null);
                sessionStorage.removeItem('userCoordinates');
            }
        };

        navigator.permissions.query({ name: 'geolocation' })
            .then((status) => {
                permissionStatus = status;
                syncPermission(status);
                status.onchange = () => syncPermission(status);
            })
            .catch(() => {
                setHasLocationPermission(Boolean(coordinates));
                setLocationPermissionState(coordinates ? 'granted' : 'prompt');
            });

        return () => {
            if (permissionStatus) permissionStatus.onchange = null;
        };
    }, []);

    // Function to manually set city
   const setManualCity = useCallback(async (cityName) => {
    // A city chosen from the location menu still needs a point for nearby
    // filtering. This is forward geocoding (place name -> coordinates).
    const query = `${cityName}, India`;
    setSelectedCity(cityName);  // Add this
    setCity(cityName);           // Keep existing
    setLocationSource('selected');
    localStorage.setItem('userSelectedCity', cityName);
    localStorage.setItem('locationSource', 'selected');
    localStorage.setItem('locationPermissionAsked', 'true');
    setShowPermissionPrompt(false);
    setError(null);

    const savedCoordinates = cityCoordinates[cityName];
    if (savedCoordinates) {
        setCoordinates(savedCoordinates);
        sessionStorage.setItem('userCoordinates', JSON.stringify(savedCoordinates));
        window.dispatchEvent(new Event('locationChanged'));
        return;
    }

    try {
        const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=in&q=${encodeURIComponent(query)}`,
        );
        const [match] = await response.json();
        const latitude = Number(match?.lat);
        const longitude = Number(match?.lon);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            throw new Error('Location not found');
        }
        const nextCoordinates = { latitude, longitude };
        setCoordinates(nextCoordinates);
        sessionStorage.setItem('userCoordinates', JSON.stringify(nextCoordinates));
    } catch {
        // City search still works without a point; nearby filtering simply
        // remains unavailable until a coordinate source is chosen.
        setCoordinates(null);
        sessionStorage.removeItem('userCoordinates');
        setError('Could not find coordinates for that city');
    } finally {
        // Dispatch event for real-time updates after coordinates are stored.
        window.dispatchEvent(new Event('locationChanged'));
    }
}, []);

    // Keep every consumer (navbar, search page and location picker) aligned
    // when another instance changes the shared location.
    useEffect(() => {
        const handleLocationChanged = () => {
            const nextCity = localStorage.getItem('userSelectedCity') || null;
            setCity(nextCity);
            setSelectedCity(nextCity);
            setLocationSource(localStorage.getItem('locationSource') || 'detected');
            try {
                setCoordinates(JSON.parse(sessionStorage.getItem('userCoordinates')) || null);
            } catch {
                setCoordinates(null);
            }
        };
        const handleStorageChanged = (event) => {
            if (['userSelectedCity', 'locationSource'].includes(event.key)) handleLocationChanged();
        };
        window.addEventListener('locationChanged', handleLocationChanged);
        window.addEventListener('storage', handleStorageChanged);
        return () => {
            window.removeEventListener('locationChanged', handleLocationChanged);
            window.removeEventListener('storage', handleStorageChanged);
        };
    }, []);

    // Handle user denying location permission
    const handleDenyPermission = useCallback(() => {
        setShowPermissionPrompt(false);
        localStorage.setItem('locationPermissionAsked', 'true');
    }, []);

    const requestLocationPermission = useCallback(() => {
        setError(null);
        setShowPermissionPrompt(true);
    }, []);

    // Auto-detect location on first load if no saved city
    useEffect(() => {
        const savedCity = localStorage.getItem('userSelectedCity');
        const hasPermissionBeenAsked = localStorage.getItem('locationPermissionAsked');
        
        // Show permission modal only if:
        // 1. No city has been selected yet
        // 2. User hasn't been asked before
        if (!savedCity && !hasPermissionBeenAsked) {
            console.log('✅ Showing Location Permission Modal');
            setShowPermissionPrompt(true);
        }
    }, []);

    return {
        city,
        selectedCity,
        address,
        loading,
        error,
        locationSource, 
        coordinates,
        hasLocationPermission,
        locationPermissionState,
        detectLocation,
        setManualCity,
        showPermissionPrompt,
        requestLocationPermission,
        handleDenyPermission
    };
};

export const LocationProvider = ({ children }) => {
    const location = useGeolocationState();
    return <LocationContext.Provider value={location}>{children}</LocationContext.Provider>;
};

const useGeolocation = () => {
    const location = useContext(LocationContext);
    if (!location) throw new Error('useGeolocation must be used within LocationProvider');
    return location;
};

export default useGeolocation;
